import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../../services/authService';
import type { User } from '../../types/auth';

/**
 * 認証コンテキスト
 */

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * 初期化時に認証状態を確認
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        setIsLoading(true);

        if (authService.isAuthenticated()) {
          const currentUser = await authService.getCurrentUser();

          if (currentUser) {
            setUser(currentUser);
            setIsAuthenticated(true);
          } else {
            setUser(null);
            setIsAuthenticated(false);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  /**
   * ログイン処理
   */
  const login = useCallback(async (email: string, password: string): Promise<void> => {
    try {
      setIsLoading(true);

      const response = await authService.login(email, password);

      if (response.success && response.user) {
        setUser(response.user);
        setIsAuthenticated(true);
        navigate('/dashboard');
      } else {
        throw new Error(response.error || 'ログインに失敗しました');
      }
    } catch (error: unknown) {
      const err = error as Error;
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  /**
   * ログアウト処理
   */
  const logout = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);

      await authService.logout();

      setUser(null);
      setIsAuthenticated(false);
      navigate('/login');
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
      navigate('/login');
    } finally {
      setIsLoading(false);
    }
  }, [navigate]);

  /**
   * ユーザー情報の再取得
   */
  const refreshUser = useCallback(async (): Promise<void> => {
    try {
      setIsLoading(true);

      const currentUser = await authService.getCurrentUser();

      if (currentUser) {
        setUser(currentUser);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    login,
    logout,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
